import { MASTER_FEATURE_FLAGS } from '@alpha-core/feature-flags/master-feature-flags.config';
import {
    cacheInterceptor,
    CacheService,
    ProjectService,
    sessionResetInterceptor,
    SessionTimeoutService,
} from '@alpha-core/platform-ui';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withRouterConfig } from '@angular/router';
import { FEATURE_FLAGS_CONFIG_TOKEN } from '@neutrinos/ngx-feature-flags';
import { NgIdleModule } from '@ng-idle/core';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { lastValueFrom } from 'rxjs';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { adminCacheConfig } from './cache.config';
import { userService } from './services/user-service';
import { authInterceptor } from './shared/interceptors/auth.interceptor';
import { ProjectResolverService } from './shared/resolvers/project.resolver';

async function appInitializer(
    cacheService: CacheService,
    userService: userService,
    sessionTimeoutService: SessionTimeoutService,
): Promise<void> {
    const res = await lastValueFrom(userService.getUser());
    if (!res) return;
    await cacheService.appInitializer(adminCacheConfig);
    await sessionTimeoutService.initializeSessionTimeout(true);
}

window.environments = environment;

export const appConfig: ApplicationConfig = {
    providers: [
        ProjectResolverService,
        ProjectService,
        MessageService,
        DialogService,
        SessionTimeoutService,
        provideRouter(
            routes,
            withRouterConfig({
                canceledNavigationResolution: 'computed',
            }),
        ),
        {
            provide: APP_INITIALIZER,
            useFactory:
                (cacheService: CacheService, userService: userService, sessionTimeoutService: SessionTimeoutService) =>
                () =>
                    appInitializer(cacheService, userService, sessionTimeoutService),
            deps: [CacheService, userService, SessionTimeoutService, ProjectService],
            multi: true,
        },
        provideAnimationsAsync(),
        importProvidersFrom(NgIdleModule.forRoot()),
        provideHttpClient(withInterceptors([authInterceptor, cacheInterceptor, sessionResetInterceptor]), withFetch()),
        {
            provide: FEATURE_FLAGS_CONFIG_TOKEN,
            useValue: MASTER_FEATURE_FLAGS,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (sessionTimeoutService: SessionTimeoutService) => () =>
                sessionTimeoutService.unauthorizedListenerFactory(),
            deps: [SessionTimeoutService],
            multi: true,
        },
    ],
};
